// NumberStats.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * Hook: returns boolean visible for a DOM element ref.
 * Guarantees reliable detection on mount, fast scroll, mobile, and desktop.
 */
function useIsVisible(ref, { threshold = 0.1, rootMargin = "50px 0px", once = true } = {}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Immediate check if element is already in viewport
    const checkBounding = () => {
      try {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const vw = window.innerWidth || document.documentElement.clientWidth;
        return r.top < vh + 60 && r.bottom > -60 && r.left < vw && r.right > 0;
      } catch (e) {
        return false;
      }
    };

    if (checkBounding()) {
      setVisible(true);
      if (once) return;
    }

    let observer = null;

    if (typeof window !== "undefined" && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && (entry.isIntersecting || entry.intersectionRatio > 0)) {
            setVisible(true);
            if (once && observer) {
              observer.disconnect();
            }
          } else if (!once) {
            setVisible(false);
          }
        },
        { threshold: [0, 0.1, 0.25], rootMargin }
      );

      observer.observe(el);
    }

    // Scroll & resize listener as fallback
    const handleScrollOrResize = () => {
      if (checkBounding()) {
        setVisible(true);
        if (once) {
          window.removeEventListener("scroll", handleScrollOrResize);
          window.removeEventListener("resize", handleScrollOrResize);
          if (observer) observer.disconnect();
        }
      }
    };

    window.addEventListener("scroll", handleScrollOrResize, { passive: true });
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [ref, threshold, rootMargin, once]);

  return visible;
}

/**
 * Counter component: smoothly animates from 0 -> target when visible.
 * Features:
 *   - Never gets stuck mid-way; guarantees final target is reached
 *   - Safety timeout fallback if browser pauses requestAnimationFrame
 *   - Smooth transition if target changes without abruptly resetting to 0
 */
function Counter({ target, duration = 1400, isVisible = true }) {
  const numTarget = Number.isFinite(target) ? Math.max(0, Math.round(target)) : 0;
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const targetRef = useRef(numTarget);
  const hasAnimatedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const endVal = numTarget;
    const prevTarget = targetRef.current;
    targetRef.current = endVal;

    // If not in view yet, keep 0 and wait until visible
    if (!isVisible) {
      return;
    }

    // If already animated to this exact target, stay there
    if (hasAnimatedRef.current && countRef.current === endVal && prevTarget === endVal) {
      return;
    }

    const startVal = countRef.current;
    const diff = endVal - startVal;

    // If no change needed, ensure count matches endVal
    if (diff === 0) {
      setCount(endVal);
      countRef.current = endVal;
      hasAnimatedRef.current = true;
      return;
    }

    hasAnimatedRef.current = true;
    let startTime = null;
    const animDuration = Math.min(Math.max(duration, 800), 1600);

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / animDuration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startVal + diff * eased);

      setCount(current);
      countRef.current = current;

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setCount(endVal);
        countRef.current = endVal;
        rafRef.current = null;
      }
    };

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(step);

    // Guaranteed fallback: ensures count NEVER stays stuck if RAF is throttled or interrupted
    const fallbackTimer = setTimeout(() => {
      setCount(endVal);
      countRef.current = endVal;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    }, animDuration + 200);

    return () => {
      clearTimeout(fallbackTimer);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [numTarget, isVisible, duration]);

  return (
    <div className="counter text-white" aria-hidden="true">
      {Number.isFinite(target) ? count.toLocaleString() : "\u00A0"}
    </div>
  );
}

const STATIC_FALLBACK_STATS = [
  { id: 1, key: "grooms", icon: "/assets/img/icons/staticon1.png", defaultTarget: 2192, label: "Grooms" },
  { id: 2, key: "brides", icon: "/assets/img/icons/staticon2.png", defaultTarget: 155, label: "Brides" },
  { id: 3, key: "divorcee_grooms", icon: "/assets/img/icons/staticon3.png", defaultTarget: 151, label: "Divorcee Grooms" },
  { id: 4, key: "divorcee_brides", icon: "/assets/img/icons/staticon4.png", defaultTarget: 16, label: "Divorcee Brides" },
  { id: 5, key: "success_stories", icon: "/assets/img/icons/staticon5.png", defaultTarget: 56, label: "Success Stories" },
];

export default function NumberStats({ homeData }) {
  const sectionRef = useRef(null);
  const isVisible = useIsVisible(sectionRef, { threshold: 0.1, rootMargin: "50px 0px", once: true });
  const apiBase = (process.env.REACT_APP_BASE_URL_API || "").replace(/\/$/, "");

  const resolveIcon = (iconPath, defaultIcon) => {
    if (!iconPath) return defaultIcon;
    if (iconPath.startsWith("http://") || iconPath.startsWith("https://")) return iconPath;
    if (iconPath.startsWith("assets/") || iconPath.startsWith("/assets/")) return iconPath.replace(/^\//, "");
    if (iconPath.startsWith("/")) return `${apiBase}${iconPath}`;
    return `${apiBase}/${iconPath}`;
  };

  let stats = [];

  if (Array.isArray(homeData?.numberStats) && homeData.numberStats.length > 0) {
    stats = homeData.numberStats.map((item, index) => ({
      id: item._id || item.key || index,
      icon: resolveIcon(item.icon, `/assets/img/icons/staticon${(index % 5) + 1}.png`),
      target: Number(item.target ?? (STATIC_FALLBACK_STATS[index]?.defaultTarget ?? 0)),
      label: item.label,
    }));
  } else {
    stats = [
      {
        id: 1,
        icon: "/assets/img/icons/staticon1.png",
        target: homeData?.totalGrooms !== undefined && homeData?.totalGrooms !== null ? Number(homeData.totalGrooms) : 2192,
        label: "Grooms",
      },
      {
        id: 2,
        icon: "/assets/img/icons/staticon2.png",
        target: homeData?.totalBrides !== undefined && homeData?.totalBrides !== null ? Number(homeData.totalBrides) : 155,
        label: "Brides",
      },
      {
        id: 3,
        icon: "/assets/img/icons/staticon3.png",
        target: homeData?.totalDivorceeGrooms !== undefined && homeData?.totalDivorceeGrooms !== null ? Number(homeData.totalDivorceeGrooms) : 151,
        label: "Divorcee Grooms",
      },
      {
        id: 4,
        icon: "/assets/img/icons/staticon4.png",
        target: homeData?.totalDivorceeBrides !== undefined && homeData?.totalDivorceeBrides !== null ? Number(homeData.totalDivorceeBrides) : 16,
        label: "Divorcee Brides",
      },
      {
        id: 5,
        icon: "/assets/img/icons/staticon5.png",
        target: homeData?.totalSuccessStories !== undefined && homeData?.totalSuccessStories !== null ? Number(homeData.totalSuccessStories) : 56,
        label: "Success Stories",
      },
    ];
  }

  return (
    <section ref={sectionRef} className="numberstats overflow-hidden bg2 pt-50 pb-50">
      <div className="site-width">
        <div className="row justify-content-center">
          {stats.map((stat) => (
            <div className="col-6 col-lg" key={stat.id}>
              <div className="statcol my-2 text-center">
                <img
                  src={stat.icon}
                  alt={stat.label}
                  className="mx-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/img/icons/staticon1.png";
                  }}
                />
                <Counter
                  target={stat.target}
                  duration={1400}
                  isVisible={isVisible}
                />
                <h4 className="text-white">{stat.label}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
